import Hamburger from 'hamburger-react';
import { useSelector } from 'react-redux';
import { ChevronRight, ShoppingCart, User, X } from 'lucide-react';
import { getTotalCartQuantity } from '../../../features/cart/cartSlice';
import { getUserIsAuthenticated } from '../../../features/auth/authSlice';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import CartDropdownItems from './CartDropdownItems';
import UserMenuDropdown from './UserMenuDropdown';
import Shop from './Shop';
import Logo from '../Logo';
import { useLogout } from '../../../hooks/useLogout';

export const MobileNavBar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [userMenuIsOpen, setUserMenuIsOpen] = useState(false);
  const [hamburgerIsOpen, setHamburgerIsOpen] = useState(false);
  const [shopIsOpen, setShopIsOpen] = useState(false);
  const totalProductsInCart = useSelector(getTotalCartQuantity);
  const isAuthenticated = useSelector(getUserIsAuthenticated);
  const logout = useLogout();

  const hanldeCartDropdownItems = () => {
    setIsOpen((prev) => !prev);
  };

  const handleLogOutUser = () => {
    logout({ onClose: () => setHamburgerIsOpen(false) });
  };

  const handleCloseAll = () => {
    setHamburgerIsOpen(false);
    setShopIsOpen(false);
  };

  useEffect(() => {
    document.body.style.overflow = hamburgerIsOpen ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [hamburgerIsOpen]);

  return (
    <>
      <nav aria-label="Main navigation">
        <ul className="flex items-center gap-4">
          <li>
            <User
              onClick={() => setUserMenuIsOpen(!userMenuIsOpen)}
              className="cursor-pointer"
            />
          </li>

          <li>
            <div className="relative" onClick={hanldeCartDropdownItems}>
              {totalProductsInCart > 0 && (
                <div className="bg-[#FB0202] text-white text-[11px] font-medium p-1 rounded-full absolute bottom-[14px] left-3 min-w-6 flex justify-center">
                  {totalProductsInCart}
                </div>
              )}
              <button aria-label="View shopping cart">
                <ShoppingCart size={26} aria-hidden="true" />
              </button>
            </div>
            <div className="absolute right-0 top-[4.02rem] min-w-[320px] max-w-[412px] bg-gray-700">
              <CartDropdownItems isOpen={isOpen} setIsOpen={setIsOpen} />
            </div>
          </li>

          <li>
            <Hamburger toggled={hamburgerIsOpen} toggle={setHamburgerIsOpen} />
          </li>
        </ul>
      </nav>

      {hamburgerIsOpen && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={handleCloseAll}
          />
          <div className="absolute inset-0 bg-white flex flex-col">
            <div className="flex items-center justify-between px-5 pt-5 pb-4">
              <Logo />
              <button onClick={handleCloseAll}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <ul className="flex-1 overflow-y-auto px-5 pt-4 space-y-6">
              <li>
                <button
                  className="flex items-center justify-between w-full text-left"
                  onClick={() => setShopIsOpen(true)}
                >
                  <span className="font-semibold text-xl">Shop</span>
                  <ChevronRight className="w-5 h-5 text-gray-900" />
                </button>
              </li>
              <li className="relative group w-fit">
                <span className="font-semibold text-xl opacity-50 cursor-not-allowed pointer-events-none">
                  Gift card
                </span>
                <div className="absolute left-0 -top-7 text-black text-xs bg-[#FFDE11] px-3 py-[2px] rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-30">
                  Coming soon
                  <div className="absolute left-4 top-full w-2 h-2 bg-[#FFDE11] rotate-45" />
                </div>
              </li>
              <li className="relative group w-fit">
                <span className="font-semibold text-xl opacity-50 cursor-not-allowed pointer-events-none">
                  Sell
                </span>
                <div className="absolute left-0 -top-7 text-black text-xs bg-[#FFDE11] px-3 py-[2px] rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap z-30">
                  Coming soon
                  <div className="absolute left-4 top-full w-2 h-2 bg-[#FFDE11] rotate-45" />
                </div>
              </li>
              <li>
                <a
                  href="https://help.smallsmall.com/smallsmall/directories"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-xl"
                  onClick={handleCloseAll}
                >
                  Help
                </a>
              </li>
            </ul>

            <div className="px-5 pb-8">
              <hr className="mb-6" />
              {isAuthenticated ? (
                <button
                  className="w-full bg-[#fde047] rounded-[10px] py-4 font-bold flex items-center justify-center gap-2"
                  onClick={handleLogOutUser}
                >
                  Log out
                  <img
                    src="/images/logout-icon.svg"
                    alt=""
                    aria-hidden="true"
                  />
                </button>
              ) : (
                <div className="flex items-center justify-center gap-2 bg-[#F5F5F7] rounded-2xl py-4">
                  <Link
                    to="/login"
                    className="font-semibold text-gray-900"
                    onClick={handleCloseAll}
                  >
                    Log in
                  </Link>
                  <span className="text-gray-400">or</span>
                  <Link
                    to="/sign-up"
                    className="font-semibold text-gray-900"
                    onClick={handleCloseAll}
                  >
                    Create account
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Shop
        isOpen={shopIsOpen}
        onClose={() => setShopIsOpen(false)}
        onCloseAll={handleCloseAll}
      />

      {userMenuIsOpen && (
        <UserMenuDropdown
          userMenuIsOpen={userMenuIsOpen}
          setUserMenuIsOpen={setUserMenuIsOpen}
        />
      )}
    </>
  );
};
